// src/components/Navbar.jsx
import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import useScrollSpy from "../hooks/useScrollSpy";
import { HiHome, HiBriefcase, HiCollection, HiMail, HiDocumentText } from "react-icons/hi";

const HOME_SECTIONS = ["hero", "preview"];

// Tight, centered sizing
const PILL = 50;         // pill height (px)
const BASE_W = 56;       // width at rest
const EXPANDED_W = 188;  // width on hover

export default function Navbar() {
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  const activeSection = useScrollSpy(onHome ? HOME_SECTIONS : []);

  // top progress bar
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 20, mass: 0.3 });

  const links = useMemo(
    () => [
      { key: "home",       label: "Home",       to: "/",            section: "hero",       isAnchor: true,  Icon: HiHome,         i: "#fcd34d", j: "#f97316" },
      { key: "timeline",   label: "Timeline",   to: "/timeline",    section: undefined,    isAnchor: false, Icon: HiBriefcase,    i: "#a78bfa", j: "#7c3aed" },
      { key: "projects",   label: "Projects",   to: "/projects",    section: undefined,    isAnchor: false, Icon: HiCollection,   i: "#34d399", j: "#16a34a" },
      { key: "resume",     label: "Resume",     to: "/resume",      section: undefined,    isAnchor: false, Icon: HiDocumentText, i: "#60a5fa", j: "#3b82f6" },
      { key: "contact",    label: "Contact",    to: "/contact",     section: undefined,    isAnchor: false, Icon: HiMail,         i: "#fb7185", j: "#ef4444" },
    ],
    []
  );

  const activeKey = onHome
    ? links.find((l) => l.section === activeSection)?.key || "home"
    : pathname === "/contact"
    ? "contact"
    : pathname === "/projects"
    ? "projects"
    : pathname === "/timeline"
    ? "timeline"
    : pathname === "/resume"
    ? "resume"
    : "home";

  return (
    <>
      <motion.div
        className="fixed left-0 right-0 top-0 z-[60] h-[2px] origin-left bg-gradient-to-r from-amber-300 to-orange-500 dark:from-amber-400 dark:to-orange-600"
        style={{ scaleX: progress }}
      />
      {/* Lifted a bit so it never hides the footer CTA area */}
      <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[55] flex justify-center">
        <Dock links={links} activeKey={activeKey} onHome={onHome} />
      </div>
    </>
  );
}

/* ------------------------- Dock ------------------------- */
function Dock({ links, activeKey, onHome }) {
  const [hoveringDock, setHoveringDock] = useState(false);

  return (
    <motion.nav
      aria-label="Primary"
      onHoverStart={() => setHoveringDock(true)}
      onHoverEnd={() => setHoveringDock(false)}
      initial={false}
      animate={{
        y: hoveringDock ? -2 : 0,
        boxShadow: hoveringDock ? "0 16px 40px rgba(15,23,42,0.22)" : "0 12px 32px rgba(15,23,42,0.12)",
        filter: hoveringDock ? "brightness(1.02)" : "brightness(1)",
      }}
      transition={{ type: "spring", stiffness: 350, damping: 28, mass: 0.6 }}
      className="pointer-events-auto rounded-full border border-white/60 dark:border-slate-700/60 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl px-2 py-1"
      style={{ boxShadow: "0 12px 32px rgba(15,23,42,0.12)" }}
    >
      <ul className="relative flex items-center gap-1">
        {links.map((props) => (
          <DockItem key={props.key} {...props} active={activeKey === props.key} onHome={onHome} />
        ))}
      </ul>
    </motion.nav>
  );
}

/* -------------------- Dock Item (unified hover: swipe + rotate) -------------------- */
function DockItem({ label, to, isAnchor, active, Icon, onHome, i, j }) {
  const [hover, setHover] = useState(false);

  const Wrapper = isAnchor ? "a" : NavLink;
  const wrapperProps = isAnchor
    ? { href: to, "aria-current": onHome && active ? "page" : undefined }
    : { to };

  const gradientStyle = { "--i": i, "--j": j };

  return (
    <motion.li
      className="relative"
      initial={false}
      animate={{
        width: hover ? EXPANDED_W : BASE_W,
        height: PILL,
        boxShadow: hover ? "0 10px 25px rgba(0,0,0,0)" : "0 10px 25px rgba(0,0,0,0.10)",
      }}
      transition={{ type: "spring", stiffness: 400, damping: 30, mass: 0.6 }}
      onHoverStart={() => setHover(true)}
      onHoverEnd={() => setHover(false)}
      style={{ borderRadius: 9999 }}
    >
      {/* Active bubble (slightly darker) */}
      {active && (
        <motion.div
          layoutId="dockActive"
          className="absolute inset-0 rounded-full border-2 z-[1] bg-white/95 dark:bg-slate-600 border-white/90 dark:border-slate-500 shadow-lg dark:shadow-slate-900/60"
          initial={false}
          transition={{ type: "spring", stiffness: 500, damping: 32 }}
        />
      )}

      {/* Swipe background (same language as footer): slides up on hover */}
      <div className="absolute inset-0 rounded-full z-[2] overflow-hidden">
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: "linear-gradient(45deg, var(--i), var(--j))",
            ...gradientStyle,
          }}
          initial={false}
          animate={{ y: hover ? "0%" : "100%" }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        />
      </div>

      {/* Blurred glow under pill */}
      <motion.div
        className="absolute -z-10 rounded-full"
        style={{
          top: 8,
          width: "100%",
          height: "100%",
          filter: "blur(14px)",
          borderRadius: 9999,
          background: "linear-gradient(45deg, var(--i), var(--j))",
          ...gradientStyle,
        }}
        initial={false}
        animate={{ opacity: hover ? 0.5 : 0 }}
        transition={{ duration: 0.25 }}
      />

      {/* Clickable area fills the pill */}
      <Wrapper
        {...wrapperProps}
        className="relative z-[3] grid h-full w-full place-items-center rounded-full focus:outline-none"
        aria-current={!isAnchor && active ? "page" : undefined}
      >
        <div className="relative h-full w-full">
          {/* Icon (centered; rotate like footer, then fade/scale out) */}
          <motion.div
            className="absolute inset-0 grid place-items-center"
            initial={false}
            animate={{ rotate: hover ? 360 : 0, scale: hover ? 0 : 1 }}
            transition={{ rotate: { duration: 0.5 }, scale: { duration: 0.22 } }}
          >
            <span
              className="flex items-center justify-center"
              style={{ width: 24, height: 24, lineHeight: 0 }}
            >
              <Icon className={`block ${active ? "text-slate-900 dark:text-slate-100" : "text-slate-600 dark:text-slate-300"}`} size={20} />
            </span>
          </motion.div>

          {/* Label (centered; fades in as swipe completes) */}
          <motion.div
            className="absolute inset-0 grid place-items-center"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: hover ? 1 : 0, opacity: hover ? 1 : 0 }}
            transition={{ duration: 0.24, delay: hover ? 0.1 : 0 }}
          >
            <span className="font-medium tracking-wide uppercase text-xs text-white">{label}</span>
          </motion.div>
        </div>

        <span className="sr-only">{label}</span>
      </Wrapper>

      {/* Optional tooltip (kept) */}
      <AnimatePresence>
        {hover && (
          <motion.div
            key="tip"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 rounded-xl bg-white/95 text-slate-900 text-xs px-3 py-1 shadow-lg border border-white/70"
            role="tooltip"
          >
            {label}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.li>
  );
}
